import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { apiOrigin } from '../apiOrigin';
import {
  ArrowRight,
  Camera,
  Car,
  Check,
  ChevronDown,
  ChevronUp,
  Circle,
  Clock3,
  ExternalLink,
  FileCheck2,
  Pencil,
  LockKeyhole,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  Star,
  Trash2,
  Truck,
  UserRound,
  UsersRound,
  X,
} from 'lucide-react';
import { Empty, Skeleton } from '../ui/kit';
import { useAuth } from '../App';
import { useLocation, useNavigate } from 'react-router-dom';
import { decisionErrorMessage } from '../lib/docReviewLogic.mjs';
import './Drivers.css';
import PageHeader from '../components/PageHeader';
import { ReviewDrawerById } from '../components/ReviewDrawer';   // FAHRER-KI-ABLAUFDATEN-BEDIENUNG-001: Prüfbereich bleibt im Reiter
import { datumUndZeit, nurDatum, uhrzeit } from '../lib/zeit';

// FAHRER-ANWESENHEIT-HINTERGRUND-001: App-Zustand im Fahrer-Detail — NUR Admin. Ohne Signal keine Behauptung.
export const presenceText = (presence, isOnline) => {
  if (!isOnline) return 'Offline';
  if (!presence) return 'Unbekannt';
  const minutes = (ms) => Math.max(0, Math.round((Date.now() - Number(ms)) / 60000));
  const base = presence.app_state === 'background'
    ? `App im Hintergrund seit ${minutes(presence.background_since_ms)} min`
    : presence.app_state === 'foreground' ? 'App im Vordergrund' : 'App-Zustand unbekannt (alte App)';
  return presence.confirm_deadline_ms
    ? `${base} · Pflicht-Tap bis ${uhrzeit(Number(presence.confirm_deadline_ms))} Uhr`
    : base;
};

const STATUS = {
  approved: { label: 'Freigegeben', color: '#547860' },
  pending: { label: 'In Prüfung', color: '#7a6680' },
  rejected: { label: 'Abgelehnt', color: '#a15a5a' },
};

const RIDE_STATUS_COLORS = {
  searching: '#4b5563',
  accepted: '#586b87',
  arrived: '#7c5a91',
  in_progress: '#9c27b0',
  completed: '#4b7c58',
  cancelled: '#b84b4b',
};

const RIDE_STATUS_LABELS = {
  searching: 'Suche',
  accepted: 'Angenommen',
  arrived: 'Angekommen',
  in_progress: 'Unterwegs',
  completed: 'Abgeschlossen',
  cancelled: 'Storniert',
};

const DOC_LABELS = {
  fuehrerschein: 'Führerschein',
  p_schein: 'P-Schein',
  personalausweis: 'Personalausweis / Aufenthaltstitel',
  profilfoto: 'Profilbild',
  versicherung: 'Versicherung',
  sonstiges: 'Sonstiges',
};

const recentStatusFilters = [
  { value: '', label: 'Alle aktuellen' },
  { value: 'in_progress', label: 'Unterwegs' },
  { value: 'completed', label: 'Abgeschlossen' },
  { value: 'cancelled', label: 'Storniert' },
];

const DIRECTORY_PAGE_SIZE = 25;

const fmtDate = (value, withTime = false) => (withTime ? datumUndZeit(value) : nurDatum(value));

const initials = (name) => String(name || '?')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map(part => part.charAt(0))
  .join('')
  .toUpperCase();

const money = (value) => Number(value || 0).toLocaleString('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const ChecklistChip = ({ ok, label }) => (
  <span className={`drivers-check-chip ${ok ? 'is-complete' : ''}`}>
    {ok ? <Check size={12} /> : <Circle size={12} />} {label}
  </span>
);

// FAHRER-KI-BACKEND-001 (Teil D): Klartext der Sperrart in Liste und Akte.
const SUSPENSION_TONE = { admin: 'is-admin', selfie: 'is-selfie', dokument: 'is-dokument', compliance: 'is-dokument', sonstige: 'is-dokument' };
const fmtEventTime = (value) => datumUndZeit(value, { jahr: 'voll', leer: '–' });

function AccountLockSection({ driver, canManage, onLock, onUnlock }) {
  const [reasonCode, setReasonCode] = useState('sonstiges');
  const [reason, setReason] = useState('');
  const [unlockReason, setUnlockReason] = useState('');
  const [busy, setBusy] = useState(false);
  const locks = driver.account_locks || {};
  const active = locks.active || {};
  const codes = locks.reason_codes || [];
  const submit = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };
  return (
    <section className="drivers-account-lock">
      <span className="drivers-overline">Kontosperre · Admin</span>
      {locks.suspension ? (
        <div className={`drivers-lock-state ${SUSPENSION_TONE[locks.suspension.kind] || ''}`}>
          <LockKeyhole size={13} />
          <strong>{locks.suspension.label}</strong>
          {active.admin && <span>Grund: {active.admin.reason || '–'} · {active.admin.actor_name || 'Admin'} · {fmtEventTime(active.admin.created_at)}</span>}
          {active.selfie && <span>Selfie-Prüfung offen (Verifizierung #{active.selfie.verification_id}) — Entscheidung im Selfie-Vergleich</span>}
          {!active.admin && !active.selfie && <span>Automatische Sperre des Ablauf-Wächters — löst sich nach gültiger Freigabe.</span>}
        </div>
      ) : (
        <div className="drivers-lock-state is-free"><ShieldCheck size={13} /><strong>Nicht gesperrt</strong></div>
      )}
      {canManage && (active.admin ? (
        <div className="drivers-lock-form">
          <input value={unlockReason} onChange={(event) => setUnlockReason(event.target.value)} placeholder="Grund für das Entsperren (wird protokolliert)" aria-label="Grund für das Entsperren" />
          <button className="is-unlock" disabled={busy} onClick={() => submit(() => onUnlock(driver.id, unlockReason))}>Entsperren</button>
        </div>
      ) : (
        <div className="drivers-lock-form">
          <select value={reasonCode} onChange={(event) => setReasonCode(event.target.value)} aria-label="Art des Grundes">
            {codes.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            {codes.length === 0 && <option value="sonstiges">Sonstiges</option>}
          </select>
          <input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Sperrgrund (Pflicht) — der Fahrer sieht ihn" aria-label="Sperrgrund" />
          <button className="is-lock" disabled={busy || !reason.trim()} onClick={() => submit(() => onLock(driver.id, { reason: reason.trim(), reason_code: reasonCode }))}>Konto sperren</button>
        </div>
      ))}
      {(locks.events || []).length > 0 && (
        <div className="drivers-lock-events">
          <span className="drivers-overline">Protokoll · unveränderlich</span>
          {locks.events.slice().reverse().map((ev) => (
            <div key={ev.id}>
              <strong>{ev.action === 'lock' ? 'Gesperrt' : 'Entsperrt'} · {ev.kind === 'selfie' ? 'Selfie' : 'Admin'}</strong>
              <span>{ev.reason || '–'}</span>
              <small>{ev.actor_name || (ev.kind === 'selfie' && ev.action === 'lock' ? 'System' : 'Admin')} · {fmtEventTime(ev.created_at)}</small>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function ManagementDriverDetail({ state, onRetry, onNavigateDocument, onChangeAvatar, onChangeName, onOpenAkte, canManageLocks, onLock, onUnlock }) {
  if (state.status === 'loading' || state.status === 'idle') {
    return (
      <div className="drivers-detail-status" role="status">
        <RefreshCw size={13} /> Fahrerdetails werden geladen …
      </div>
    );
  }

  if (state.status === 'error' || !state.data) {
    return (
      <div className="drivers-detail-status is-error">
        <span>Fahrerdetails konnten nicht geladen werden.</span>
        <button onClick={onRetry}><RefreshCw size={12} /> Erneut versuchen</button>
      </div>
    );
  }

  const driver = state.data;
  return (
    <>
      {onOpenAkte && (
        <section className="drivers-detail-akte-link">
          <button type="button" onClick={onOpenAkte}>Fahrer-Akte öffnen</button>
          <small>Überblick mit Verdienst und Korrekturbuchung, Fahrten, Dokumente und Support-Historie an einer Stelle.</small>
        </section>
      )}
      <section>
        <span className="drivers-overline">Kontakt & Betrieb</span>
        <div className="drivers-contact-grid">
          <span><small>E-Mail</small><strong>{driver.email || 'Nicht hinterlegt'}</strong></span>
          <span><small>Telefon</small><strong>{driver.phone || 'Nicht hinterlegt'}</strong></span>
          <span><small>Partner</small><strong>{driver.partner_company || driver.company_name || 'Kein Partner'}</strong></span>
          <span><small>Verfügbarkeit</small><strong>{driver.is_online ? 'Online' : 'Offline'}</strong></span>
          <span data-testid="driver-presence"><small>App-Zustand</small><strong>{presenceText(driver.presence, driver.is_online)}</strong></span>
        </div>
      </section>

      <section>
        <span className="drivers-overline">Freigabe-Checkliste</span>
        <div className="drivers-checklist">
          <ChecklistChip ok={driver.onboarding?.agbAccepted} label="AGB akzeptiert" />
          {(driver.onboarding?.documents || []).map(item => (
            <ChecklistChip
              key={item.type}
              ok={item.done}
              label={`${item.label}${item.status === 'rejected'
                ? ' (abgelehnt)'
                : item.status === 'pending' ? ' (Prüfung)' : ''}`}
            />
          ))}
        </div>
      </section>

      <section>
        <span className="drivers-overline">Dokumente</span>
        {(driver.documents || []).length === 0 ? (
          <span className="drivers-no-docs">Keine Dokumente</span>
        ) : (
          <div className="drivers-documents">
            {(driver.documents || []).map(document => document.has_private_asset ? (
              <button key={document.id} onClick={() => onNavigateDocument(document.id)}>
                <i className={`is-${document.status}`} />
                {document.display_name || DOC_LABELS[document.document_type] || document.document_type}
                <ExternalLink size={10} />
              </button>
            ) : (
              <span key={document.id}>
                <i className={`is-${document.status}`} />
                {document.display_name || DOC_LABELS[document.document_type] || document.document_type} · kein Inhalt
              </span>
            ))}
          </div>
        )}
      </section>

      {/* FAHRERNAME-KORREKTUR-001: Bisher gab es keinen Weg, einen Tippfehler im Namen zu
          korrigieren — er blieb im Verzeichnis, in der Akte, auf Belegen und im Auftragsbuch
          stehen. Gleiches Muster wie der Profilfoto-Override darunter: Grund ist Pflicht und
          wird mit dem alten Namen protokolliert. */}
      <section>
        <span className="drivers-overline">Name · auditierter Admin-Override</span>
        <div className="drivers-name-override">
          <strong>{driver.name || 'Unbenannter Fahrer'}</strong>
          <button type="button" onClick={() => onChangeName(driver.id, driver.name)}>
            <Pencil size={12} /> Name korrigieren
          </button>
          <small>Grund ist Pflicht und wird protokolliert.</small>
        </div>
      </section>

      {driver.account_locks && <AccountLockSection driver={driver} canManage={!!canManageLocks} onLock={onLock} onUnlock={onUnlock} />}

      <section>
        <span className="drivers-overline">Profilfoto · auditierter Admin-Override</span>
        <div className="drivers-avatar-override">
          {driver.avatar ? (
            <img src={`${apiOrigin()}${driver.avatar}`} alt="" />
          ) : (
            <span className="drivers-avatar-empty">kein Foto</span>
          )}
          <label>
            <Camera size={12} /> Foto ändern
            <input
              type="file"
              accept="image/*"
              onChange={event => {
                const file = event.target.files?.[0];
                event.target.value = '';
                onChangeAvatar(driver.id, file);
              }}
            />
          </label>
          <small>Nur hier änderbar; ein protokollierter Grund ist Pflicht.</small>
        </div>
      </section>
    </>
  );
}

// ADMIN-UX-HEUTE-AKTEN-EINHEIT-001: Status-Klartext der Support-Historie in der Akte.
const SUPPORT_STATUS_LABELS = {
  neu: 'Neu', in_bearbeitung: 'In Bearbeitung', geloest: 'Gelöst', nicht_geloest: 'Nicht gelöst',
  open: 'Offen', closed: 'Geschlossen',
};

// KORREKTURBUCHUNG-ADMIN-001: freie Korrekturbuchung (± €) auf das Partnerkonto des Fahrers.
// Sichtbar nur mit finance.manage; der Server erzwingt dasselbe Recht. Der Fahrer sieht die
// Korrektur getrennt vom Fahrtumsatz und den serverseitig korrigierten Gesamtwert; der Grund
// bleibt ausschließlich im geschützten Finanz-Audit.
// REVIEW-FIX R3-P3: Betrag STRIKT parsen — höchstens zwei Nachkommastellen, sonst ablehnen.
// Vorher wurde 1,005 still zu 100 Cent gerundet; stilles Runden ist bei Geld ein Fehlerbild.
const parseEuroCents = (raw) => {
  const value = String(raw).trim().replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(value)) return null;
  const cents = Math.round(Number(value) * 100);
  return cents > 0 ? cents : null;
};

// Idempotenz-Schlüssel für Korrekturbuchungen: crypto.randomUUID mit Fallback
// (ältere Testumgebungen haben kein globales crypto).
const newCorrectionKey = () => (typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `korrektur-${Date.now()}-${Math.random().toString(16).slice(2)}`);

// Der deterministische Scope-Token trennt Admin und Ziel, ohne ihre IDs lesbar abzulegen.
// Eine theoretische Hash-Kollision bleibt durch den serverseitigen Request-Fingerprint
// fail-closed (409), statt eine andere Korrektur auszuführen.
const opaqueDriverCorrectionScope = (...parts) => {
  const input = parts.map(part => `${String(part).length}:${String(part)}`).join('|');
  let left = 0x811c9dc5;
  let right = 0x9e3779b9;
  for (let index = 0; index < input.length; index += 1) {
    const code = input.charCodeAt(index);
    left = Math.imul(left ^ code, 0x01000193);
    right = Math.imul(right ^ code, 0x85ebca6b);
    right ^= right >>> 13;
  }
  return `${(left >>> 0).toString(36)}-${(right >>> 0).toString(36)}`;
};

// Nur der Idempotency-Key wird tablokal persistiert; Betrag, Grund, Namen, Kontaktdaten
// und technische IDs bleiben allein im flüchtigen React-State.
const driverCorrectionStorageName = (adminId, driverId) => (
  adminId && driverId
    ? `voogo.admin.finance-correction.v1:driver:${opaqueDriverCorrectionScope(adminId, driverId)}`
    : null
);

const storedDriverCorrectionKey = (storageName) => {
  if (storageName) {
    try {
      const stored = sessionStorage.getItem(storageName);
      if (stored) return stored;
    } catch {}
  }
  const created = newCorrectionKey();
  if (storageName) {
    try { sessionStorage.setItem(storageName, created); } catch {}
  }
  return created;
};

const clearStoredDriverCorrectionKey = (storageName, key) => {
  if (!storageName) return;
  try {
    if (sessionStorage.getItem(storageName) === key) sessionStorage.removeItem(storageName);
  } catch {}
};

const correctionResultIsUnclear = (requestError) => {
  const status = requestError?.response?.status;
  return !requestError?.response || !Number.isFinite(status) || status === 408 || status >= 500;
};

function DriverCorrectionCard({ driverId, onBooked }) {
  const { can, user } = useAuth();
  const allowed = Boolean(can && can('finance.manage'));
  const [direction, setDirection] = useState('abzug');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const storageNameRef = useRef(null);
  const idempotencyRef = useRef(null);
  const inFlightRef = useRef(false);
  if (!allowed) return null;

  const submit = async (event) => {
    event.preventDefault();
    // React-State deaktiviert den Knopf erst im nächsten Render. Das Ref schließt deshalb
    // auch zwei synchrone Submit-Events mit demselben Formular zuverlässig aus.
    if (inFlightRef.current) return;
    const parsed = parseEuroCents(amount);
    if (parsed === null) { toast.error('Betrag in Euro mit höchstens zwei Nachkommastellen angeben, z. B. 4,00'); return; }
    if (!reason.trim()) { toast.error('Grund ist Pflicht'); return; }

    const storageName = driverCorrectionStorageName(user?.id, driverId);
    if (storageNameRef.current !== storageName) {
      storageNameRef.current = storageName;
      idempotencyRef.current = null;
    }
    const requestKey = idempotencyRef.current || storedDriverCorrectionKey(storageName);
    idempotencyRef.current = requestKey;
    inFlightRef.current = true;
    setSaving(true);
    try {
      await api.post('/admin/finance/corrections', {
        driver_id: driverId,
        amount_cents: direction === 'abzug' ? -parsed : parsed,
        reason: reason.trim(),
      }, { headers: { 'Idempotency-Key': requestKey } });
      toast.success(direction === 'abzug' ? 'Abzug gebucht — wirkt auf die nächste Partner-Auszahlung' : 'Gutschrift gebucht — wirkt auf die nächste Partner-Auszahlung');
      clearStoredDriverCorrectionKey(storageName, requestKey);
      if (idempotencyRef.current === requestKey) idempotencyRef.current = null;
      setAmount('');
      setReason('');
      if (onBooked) onBooked();
    } catch (requestError) {
      if (correctionResultIsUnclear(requestError)) {
        toast.error('Ergebnis unklar – die Buchung wurde nicht sicher bestätigt. Bitte erneut senden; derselbe Vorgangsschlüssel wird wiederverwendet.');
      } else {
        toast.error(requestError.response?.data?.message || 'Korrektur abgelehnt');
      }
    } finally {
      inFlightRef.current = false;
      setSaving(false);
    }
  };

  return (
    <section className="drivers-korrektur-card" aria-label="Korrekturbuchung">
      <span className="drivers-overline">Korrekturbuchung (Finanzbuch)</span>
      <p>Bucht eine eigene, unveränderliche Buchungszeile auf das Partnerkonto dieses Fahrers — z. B. eine reklamierte Wartezeit abziehen. Das Backend liefert dem Fahrer Fahrtumsatz, Korrektur und den korrigierten Gesamtwert getrennt; der Partner sieht die Korrektursumme in Portal und Abrechnung. Der Grund bleibt im Finanz-Audit.</p>
      <form onSubmit={submit}>
        <select value={direction} onChange={(event) => setDirection(event.target.value)} aria-label="Richtung">
          <option value="abzug">Abziehen (−)</option>
          <option value="gutschrift">Gutschreiben (+)</option>
        </select>
        <input
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          placeholder="Betrag in €, z. B. 4,00"
          inputMode="decimal"
          aria-label="Betrag in Euro"
        />
        <input
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Grund (Pflicht), z. B. Reklamierte Wartezeit"
          aria-label="Grund"
        />
        <button type="submit" disabled={saving}>{saving ? 'Bucht…' : 'Buchen'}</button>
      </form>
    </section>
  );
}

function DriverVehicleAssignments({ file }) {
  const activeVehicle = file?.active_vehicle ?? file?.vehicle ?? null;
  const authorized = Array.isArray(file?.authorized_vehicles)
    ? file.authorized_vehicles
    : (activeVehicle ? [{ ...activeVehicle, is_active: true }] : []);
  const vehicles = activeVehicle && !authorized.some((vehicle) => vehicle.id === activeVehicle.id)
    ? [{ ...activeVehicle, is_active: true }, ...authorized]
    : authorized;

  return (
    <section className="drivers-akte-vehicle">
      <span className="drivers-overline">Fahrzeug-Zuweisungen</span>
      {vehicles.length ? (
        <div className="drivers-akte-vehicle-list">
          {vehicles.map((vehicle) => {
            const isActive = vehicle.id === activeVehicle?.id || vehicle.is_active === true;
            return (
              <article key={vehicle.id}>
                <Car size={14} />
                <strong>{vehicle.brand} {vehicle.model}</strong>
                <span>{vehicle.license_plate}</span>
                <em>{isActive ? 'Aktiv gewählt' : 'Zugewiesen, in der App wählbar'}</em>
              </article>
            );
          })}
        </div>
      ) : (
        <span className="drivers-no-docs">Kein Fahrzeug zugewiesen</span>
      )}
    </section>
  );
}

// ADMIN-UX-HEUTE-AKTEN-EINHEIT-001: Aus dem Profil-Modal wird die Fahrer-AKTE — die ganze
// Geschichte einer Person an EINER Stelle (Stripe-Muster): Überblick, Fahrten, Dokumente,
// Support-Historie. Fahrten kommen weiter aus /drivers/:id/rides, Dokumente aus ?detail_id
// (bestehende Verträge), Fahrzeug + Support aus dem neuen rein lesenden /drivers/:id/file.
function DriverDetailModal({ driverId, onClose, onNavigateDocument }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [docs, setDocs] = useState({ status: 'idle', data: null });
  const [tab, setTab] = useState('uebersicht');
  // KORREKTURBUCHUNG-ADMIN-001: nach einer Korrektur die Verdienst-Kacheln neu laden,
  // ohne Reiter/Dokumente zurückzusetzen.
  const [earningsReload, setEarningsReload] = useState(0);

  useEffect(() => {
    setTab('uebersicht');
    setDocs({ status: 'idle', data: null });
    api.get(`/admin/drivers/${driverId}/rides`)
      .then(response => { setData(response.data); setLoading(false); })
      .catch(() => setLoading(false));
    api.get(`/admin/drivers/${driverId}/file`)
      .then(response => setFile(response.data))
      .catch(() => setFile(null));
  }, [driverId]);

  useEffect(() => {
    if (earningsReload === 0) return;
    api.get(`/admin/drivers/${driverId}/rides`)
      .then(response => setData(response.data))
      .catch(() => {});
  }, [earningsReload, driverId]);

  // Dokumente erst beim Öffnen des Reiters laden (bewusster Einzelabruf wie im Verzeichnis).
  useEffect(() => {
    if (tab !== 'dokumente' || docs.status !== 'idle') return;
    setDocs({ status: 'loading', data: null });
    api.get('/admin/drivers', { params: { detail_id: driverId } })
      .then(response => setDocs({ status: 'ready', data: response.data?.driver || null }))
      .catch(() => setDocs({ status: 'error', data: null }));
  }, [tab, docs.status, driverId]);

  const tabs = [
    ['uebersicht', 'Überblick'],
    ['fahrten', `Fahrten${data ? ` (${data.rides.length})` : ''}`],
    ['dokumente', 'Dokumente'],
    ['support', `Support${file ? ` (${file.support.length})` : ''}`],
  ];

  return (
    <div className="drivers-modal-backdrop" role="dialog" aria-modal="true" aria-label="Fahrer-Akte">
      <div className="drivers-modal">
        <header>
          <div>
            {/* KONTO-TRENNUNG-001: Diese Akte zeigt ausschließlich das FAHRERkonto — auch wenn
                dieselbe Person ein Kunden- oder Partnerkonto mit derselben E-Mail hat. */}
            <span className="drivers-overline">Fahrerkonto</span>
            <h2>Fahrer-Akte</h2>
          </div>
          <button onClick={onClose} aria-label="Fahrer-Akte schließen"><X size={16} /></button>
        </header>

        {loading ? (
          <div className="drivers-modal-loading"><Skeleton h={150} r={12} /></div>
        ) : data ? (
          <div className="drivers-modal-content">
            <section className="drivers-profile-card">
              {/* FAHRER-BILD-SICHTBAR-001: das freigegebene Profilbild zeigen, wenn es eines gibt.
                  Vorher standen hier immer nur Initialen — nicht, weil kein Bild existierte,
                  sondern weil der Endpunkt das Feld nicht mitlieferte. Initialen bleiben der
                  Rückfall, solange das Profilfoto noch nicht freigegeben ist. */}
              <div className="drivers-profile-avatar">
                {data.driver?.avatar
                  ? <img src={`${apiOrigin()}${data.driver.avatar}`} alt={`Profilbild von ${data.driver?.name || 'Fahrer'}`} />
                  : initials(data.driver?.name)}
              </div>
              <div className="drivers-profile-copy">
                <h3>{data.driver?.name || 'Unbenannter Fahrer'}</h3>
                <p>{data.driver?.email || 'Keine E-Mail'}</p>
                {data.driver?.phone && <p>{data.driver.phone}</p>}
                <div className="drivers-profile-meta">
                  <span><Star size={12} /> {parseFloat(data.driver?.rating || 5).toFixed(1)}</span>
                  <span><Truck size={12} /> {data.driver?.company_name || 'Kein Partner'}</span>
                  <span className={data.driver?.is_online ? 'is-online' : ''}>
                    <i /> {data.driver?.is_online ? 'Online' : 'Offline'}
                  </span>
                  <span><ShieldCheck size={12} /> {STATUS[data.driver?.approval_status]?.label || 'In Prüfung'}</span>
                </div>
              </div>
            </section>

            <nav className="drivers-akte-tabs" aria-label="Aktenbereiche">
              {tabs.map(([value, label]) => (
                <button key={value} className={tab === value ? 'is-active' : ''} onClick={() => setTab(value)}>
                  {label}
                </button>
              ))}
            </nav>

            {tab === 'uebersicht' && (
              <>
                <section className="drivers-earnings-grid">
                  <article><small>Abgeschlossene Fahrten</small><strong>{data.earnings?.completed_count || 0}</strong></article>
                  <article><small>Kontobewegung bei VOOGO · keine Auszahlung</small><strong>{money(data.earnings?.total_netto)} €</strong></article>
                  <article><small>Brutto-Fahrten</small><strong>{money(data.earnings?.total_brutto)} €</strong></article>
                  <article><small>Trinkgeld</small><strong>{money(data.earnings?.total_tips)} €</strong></article>
                </section>
                <DriverCorrectionCard driverId={driverId} onBooked={() => setEarningsReload((value) => value + 1)} />
                <DriverVehicleAssignments file={file} />
              </>
            )}

            {tab === 'fahrten' && (
              <section className="drivers-history">
                <header>
                  <div>
                    <span className="drivers-overline">Letzte 50 Datensätze</span>
                    <h3>Fahrthistorie</h3>
                  </div>
                  <strong>{data.rides.length}</strong>
                </header>

                {data.rides.length === 0 ? (
                  <Empty icon={Car} title="Noch keine Fahrten" />
                ) : (
                  <div className="drivers-history-list">
                    {data.rides.map(ride => (
                      <article key={ride.id}>
                        <div className="drivers-history-route">
                          <span><i /> {ride.pickup_address || 'Unbekannte Abholung'}</span>
                          <span><i /> {ride.dropoff_address || 'Unbekanntes Ziel'}</span>
                        </div>
                        <div className="drivers-history-context">
                          <span>{ride.customer_name || 'Unbekannter Kunde'}</span>
                          <span>{fmtDate(ride.created_at)}</span>
                        </div>
                        <div className="drivers-history-price">
                          <strong>{money(ride.price)} €</strong>
                          <span style={{ '--driver-ride-status': RIDE_STATUS_COLORS[ride.status] || '#4b5563' }}>
                            <i /> {RIDE_STATUS_LABELS[ride.status] || ride.status}
                          </span>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            )}

            {tab === 'dokumente' && (
              docs.status === 'loading' || docs.status === 'idle' ? (
                <div className="drivers-detail-status" role="status"><RefreshCw size={13} /> Dokumente werden geladen …</div>
              ) : docs.status === 'error' || !docs.data ? (
                <div className="drivers-detail-status is-error">
                  <span>Dokumente konnten nicht geladen werden.</span>
                  <button onClick={() => setDocs({ status: 'idle', data: null })}><RefreshCw size={12} /> Erneut versuchen</button>
                </div>
              ) : (
                <>
                  <section>
                    <span className="drivers-overline">Freigabe-Checkliste</span>
                    <div className="drivers-checklist">
                      <ChecklistChip ok={docs.data.onboarding?.agbAccepted} label="AGB akzeptiert" />
                      {(docs.data.onboarding?.documents || []).map(item => (
                        <ChecklistChip
                          key={item.type}
                          ok={item.done}
                          label={`${item.label}${item.status === 'rejected'
                            ? ' (abgelehnt)'
                            : item.status === 'pending' ? ' (Prüfung)' : ''}`}
                        />
                      ))}
                    </div>
                  </section>
                  <section>
                    <span className="drivers-overline">Dokumente</span>
                    {(docs.data.documents || []).length === 0 ? (
                      <span className="drivers-no-docs">Keine Dokumente</span>
                    ) : (
                      <div className="drivers-documents">
                        {(docs.data.documents || []).map(document => document.has_private_asset ? (
                          <button key={document.id} onClick={() => onNavigateDocument(document.id)}>
                            <i className={`is-${document.status}`} />
                            {document.display_name || DOC_LABELS[document.document_type] || document.document_type}
                            <ExternalLink size={10} />
                          </button>
                        ) : (
                          <span key={document.id}>
                            <i className={`is-${document.status}`} />
                            {document.display_name || DOC_LABELS[document.document_type] || document.document_type} · kein Inhalt
                          </span>
                        ))}
                      </div>
                    )}
                  </section>
                </>
              )
            )}

            {tab === 'support' && (
              <section className="drivers-akte-support">
                <span className="drivers-overline">Support-Historie dieses Kontos</span>
                {!file ? (
                  <span className="drivers-no-docs">Historie konnte nicht geladen werden.</span>
                ) : file.support.length === 0 ? (
                  <span className="drivers-no-docs">Keine Support-Vorgänge</span>
                ) : (
                  <div className="drivers-akte-support-list">
                    {file.support.map(entry => (
                      <article key={`${entry.kind}-${entry.id}`}>
                        <strong>{entry.title}</strong>
                        <span>{entry.kind === 'ticket' ? 'Ticket' : 'Chat'} · {SUPPORT_STATUS_LABELS[entry.status] || entry.status}</span>
                        <small>{fmtDate(entry.at, true)}</small>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            )}
          </div>
        ) : (
          <div className="drivers-modal-error">Fahrer-Akte konnte nicht geladen werden.</div>
        )}
      </div>
    </div>
  );
}

export default function Drivers() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('recent');
  const [offlineBusy, setOfflineBusy] = useState(null);
  const offlinePending = useRef(false);
  const [totalDrivers, setTotalDrivers] = useState(null);

  const [recentRides, setRecentRides] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [contextDraft, setContextDraft] = useState('');
  const [contextSearch, setContextSearch] = useState('');
  const [recentStatus, setRecentStatus] = useState('');
  const [detailId, setDetailId] = useState(null);
  const location = useLocation();
  // Seitengröße 25/50/100 (Owner-Entscheid 26.08.; Server clampt auf max. 100, Default 25).
  const [pageSize, setPageSize] = useState(DIRECTORY_PAGE_SIZE);

  // ⌘K-Palette: /drivers?detail=<id> öffnet das vorhandene Fahrerprofil-Modal (auch bei
  // erneuter Navigation auf derselben Seite); /drivers?browse=1 springt in den
  // Verzeichnis-Modus (Verwaltung, ohne Filter). Ohne Parameter ändert sich nichts.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // AKTE-LINKS-FAHRT-DETAIL-001: IDs sind TEXT (UUID); die frühere Ganzzahl-Prüfung ließ jeden echten
    // Deep-Link ins Leere laufen. Erlaubt: 1–64 Zeichen aus [A-Za-z0-9_-].
    const raw = params.get('detail');
    if (raw && /^[A-Za-z0-9_-]{1,64}$/.test(raw)) setDetailId(raw);
    if (params.get('browse')) setMode('management');
  }, [location]);

  const [drivers, setDrivers] = useState([]);
  const [managementLoading, setManagementLoading] = useState(false);
  const [managementError, setManagementError] = useState(false);
  const [managementTotal, setManagementTotal] = useState(0);
  const [managementPageSize, setManagementPageSize] = useState(DIRECTORY_PAGE_SIZE);
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [managementDetail, setManagementDetail] = useState({ id: null, status: 'idle', data: null });
  const [detailReloadToken, setDetailReloadToken] = useState(0);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [newDriver, setNewDriver] = useState({ name: '', email: '', phone: '', password: '' });
  const managementRequestRef = useRef(0);
  const detailRequestRef = useRef(0);
  const loadManagementRef = useRef(null);
  const expandedRef = useRef(null);

  useEffect(() => {
    api.get('/admin/dashboard')
      .then(response => setTotalDrivers(response.data.stats?.total_drivers ?? null))
      .catch(() => setTotalDrivers(null));
  }, []);

  const loadRecent = useCallback(() => {
    setRecentLoading(true);
    const params = new URLSearchParams();
    if (contextSearch) params.set('search', contextSearch);
    if (recentStatus) params.set('status', recentStatus);
    params.set('limit', '50');
    api.get(`/admin/rides?${params}`)
      .then(response => setRecentRides(response.data.rides || []))
      .catch(() => toast.error('Fahreraktivität konnte nicht geladen werden'))
      .finally(() => setRecentLoading(false));
  }, [contextSearch, recentStatus]);

  useEffect(() => { loadRecent(); }, [loadRecent]);

  const loadManagement = useCallback(() => {
    const requestId = ++managementRequestRef.current;
    const params = { limit: pageSize, offset };
    if (filter !== 'all') params.status = filter;
    if (fromDate) params.from = fromDate;
    if (toDate) params.to = toDate;
    if (search.trim()) params.search = search.trim();
    setManagementLoading(true);
    setManagementError(false);
    return api.get('/admin/drivers', { params })
      .then(response => {
        if (managementRequestRef.current !== requestId) return;
        const payload = response.data || {};
        const nextDrivers = Array.isArray(payload.drivers) ? payload.drivers : [];
        const nextTotal = Number(payload.total);
        const nextLimit = Number(payload.limit);
        const safeTotal = Number.isFinite(nextTotal) && nextTotal >= 0 ? nextTotal : nextDrivers.length;
        const safeLimit = Number.isFinite(nextLimit) && nextLimit > 0
          ? Math.min(nextLimit, pageSize)
          : pageSize;
        if ((safeTotal === 0 && offset > 0) || (safeTotal > 0 && offset >= safeTotal)) {
          setOffset(safeTotal === 0 ? 0 : Math.floor((safeTotal - 1) / safeLimit) * safeLimit);
          return;
        }
        setDrivers(nextDrivers);
        setManagementTotal(safeTotal);
        setManagementPageSize(safeLimit);
      })
      .catch(() => {
        if (managementRequestRef.current !== requestId) return;
        setDrivers([]);
        setManagementTotal(0);
        setManagementError(true);
      })
      .finally(() => {
        if (managementRequestRef.current === requestId) setManagementLoading(false);
      });
  }, [filter, fromDate, offset, pageSize, search, toDate]);
  loadManagementRef.current = loadManagement;
  expandedRef.current = expanded;

  useEffect(() => {
    if (mode === 'management') loadManagement();
    return () => { managementRequestRef.current += 1; };
  }, [loadManagement, mode]);

  useEffect(() => {
    if (!expanded) {
      detailRequestRef.current += 1;
      setManagementDetail({ id: null, status: 'idle', data: null });
      return undefined;
    }

    const driverId = expanded;
    const requestId = ++detailRequestRef.current;
    setManagementDetail({ id: driverId, status: 'loading', data: null });
    api.get('/admin/drivers', { params: { detail_id: driverId } })
      .then(response => {
        if (detailRequestRef.current !== requestId) return;
        setManagementDetail({ id: driverId, status: 'ready', data: response.data?.driver || null });
      })
      .catch(() => {
        if (detailRequestRef.current !== requestId) return;
        setManagementDetail({ id: driverId, status: 'error', data: null });
      });

    return () => {
      if (detailRequestRef.current === requestId) detailRequestRef.current += 1;
    };
  }, [detailReloadToken, expanded]);

  const closeManagementRows = () => {
    detailRequestRef.current += 1;
    setExpanded(null);
    setRejectTarget(null);
    setRejectReason('');
  };

  const refreshManagementAfterMutation = async (driverId = null) => {
    const currentExpanded = expandedRef.current;
    if (currentExpanded && (driverId == null || currentExpanded === driverId)) {
      setDetailReloadToken(value => value + 1);
    }
    await loadManagementRef.current?.();
  };

  const selectFilter = (value) => {
    closeManagementRows();
    setFilter(value);
    setOffset(0);
  };

  const selectFromDate = (value) => {
    closeManagementRows();
    setFromDate(value);
    setOffset(0);
  };

  const selectToDate = (value) => {
    closeManagementRows();
    setToDate(value);
    setOffset(0);
  };

  const applyManagementSearch = () => {
    const next = searchDraft.trim();
    closeManagementRows();
    if (next === search && offset === 0) loadManagement();
    else {
      setSearch(next);
      setOffset(0);
    }
  };

  const resetManagementFilters = () => {
    const alreadyReset = filter === 'all' && !fromDate && !toDate && !search && offset === 0;
    closeManagementRows();
    setFilter('all');
    setFromDate('');
    setToDate('');
    setSearchDraft('');
    setSearch('');
    setOffset(0);
    if (alreadyReset) loadManagement();
  };

  const changePage = (nextOffset) => {
    closeManagementRows();
    setOffset(Math.max(0, nextOffset));
  };

  const applyContextSearch = () => {
    const next = contextDraft.trim();
    if (next === contextSearch) loadRecent();
    else setContextSearch(next);
  };

  const clearContextSearch = () => {
    setContextDraft('');
    if (contextSearch) setContextSearch('');
    else loadRecent();
  };

  const create = async () => {
    if (!newDriver.name || !newDriver.email || !newDriver.password) {
      toast.error('Name, E-Mail und Passwort sind nötig');
      return;
    }
    try {
      await api.post('/admin/drivers', newDriver);
      toast.success('Fahrer angelegt (in Prüfung — Fahrzeug/Partner zuweisen)');
      setShowNew(false);
      setNewDriver({ name: '', email: '', phone: '', password: '' });
      await refreshManagementAfterMutation();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler');
    }
  };

  const remove = async (driver) => {
    if (!window.confirm(`Fahrer „${driver.name}" und Zugang wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/drivers/${driver.id}`);
      toast.success('Fahrer gelöscht');
      if (expandedRef.current === driver.id) closeManagementRows();
      await refreshManagementAfterMutation(driver.id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler');
    }
  };

  // DOC-LIFECYCLE-001R1 (Stufe F): Fahrer-Compliance-Entscheidung NUR über die dedizierten
  // Lifecycle-Routen (…/approve|…/reject, freigaben.manage + Dokument-Gate).
  const approve = async (id) => {
    try {
      await api.patch(`/admin/drivers/${id}/approve`);
      toast.success('Fahrer freigegeben!');
      await refreshManagementAfterMutation(id);
    } catch (error) {
      toast.error(decisionErrorMessage(error.response?.data), {
        duration: 9000,
        style: { whiteSpace: 'pre-line' },
      });
    }
  };

  const setDriverOffline = async (id, name) => {
    if (offlinePending.current || !can('freigaben.manage')) return;
    const reason = window.prompt(`Grund: ${name || 'Fahrer'} offline setzen`);
    if (reason == null) return;
    if (!reason.trim() || reason.trim().length > 300) {
      toast.error('Bitte einen Grund mit 1 bis 300 Zeichen angeben.');
      return;
    }
    if (!window.confirm(
      `${name || 'Diesen Fahrer'} jetzt offline setzen?\n\n`
      + 'Offene Angebote werden zurückgezogen. Eine bereits angenommene oder laufende Fahrt bleibt bestehen und wird dadurch nicht abgeschlossen.',
    )) return;
    offlinePending.current = true;
    setOfflineBusy(id);
    try {
      await api.patch(`/admin/drivers/${id}/force-offline`, { reason: reason.trim() });
      toast.success('Fahrer offline gesetzt.');
      await refreshManagementAfterMutation(id);
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.error || 'Ergebnis unklar. Bitte den Fahrerstatus neu laden.');
    } finally {
      offlinePending.current = false;
      setOfflineBusy(null);
    }
  };

  const reject = async (id) => {
    if (!rejectReason.trim()) {
      toast.error('Ablehnungsgrund erforderlich');
      return;
    }
    try {
      await api.patch(`/admin/drivers/${id}/reject`, { reason: rejectReason });
      toast.success('Fahrer abgelehnt');
      setRejectTarget(null);
      setRejectReason('');
      await refreshManagementAfterMutation(id);
    } catch (error) {
      toast.error(decisionErrorMessage(error.response?.data), {
        duration: 9000,
        style: { whiteSpace: 'pre-line' },
      });
    }
  };

  // FAHRER-KI-BACKEND-001 (Teil D): Kontosperre mit Grund (freigaben.manage) — Entsperren ebenfalls protokolliert.
  const lockAccount = async (id, { reason, reason_code }) => {
    try {
      const response = await api.post(`/admin/drivers/${id}/account-lock`, { reason, reason_code });
      toast.success(response.data?.message || 'Konto gesperrt');
      await refreshManagementAfterMutation(id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Sperren fehlgeschlagen');
    }
  };
  const unlockAccount = async (id, reason) => {
    try {
      const response = await api.delete(`/admin/drivers/${id}/account-lock`, { data: { reason: reason || undefined } });
      toast.success(response.data?.message || 'Sperre aufgehoben');
      await refreshManagementAfterMutation(id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Entsperren fehlgeschlagen');
    }
  };

  // Profilfoto eines Fahrers ändern (Admin-Override, freigaben.manage). Grund bleibt Pflicht
  // und wird serverseitig als avatar_override auditiert.
  const changeAvatar = async (id, file) => {
    if (!file) return;
    const reason = (window.prompt('Grund für die Profilfoto-Änderung (Pflicht, wird protokolliert):') || '').trim();
    if (!reason) {
      toast.error('Ein Grund ist Pflicht');
      return;
    }
    const form = new FormData();
    form.append('file', file);
    form.append('reason', reason);
    try {
      await api.post(`/admin/drivers/${id}/avatar`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Profilfoto geändert');
      await refreshManagementAfterMutation(id);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Fehler beim Hochladen');
    }
  };

  // FAHRERNAME-KORREKTUR-001: Namen eines Fahrers korrigieren (Admin-Override, freigaben.manage).
  // Bewusst dasselbe Muster wie der Profilfoto-Override direkt darüber: Grund ist Pflicht und wird
  // serverseitig als name_override MIT dem alten Namen protokolliert.
  const changeName = async (id, aktuellerName) => {
    const neu = (window.prompt('Neuer Name des Fahrers:', aktuellerName || '') || '').trim();
    if (!neu) return;                                   // Abbruch im Dialog = nichts tun
    if (neu === (aktuellerName || '')) return;           // unverändert = kein Protokollrauschen
    const reason = (window.prompt('Grund für die Namensänderung (Pflicht, wird protokolliert):') || '').trim();
    if (!reason) {
      toast.error('Ein Grund ist Pflicht');
      return;
    }
    try {
      await api.patch(`/admin/drivers/${id}/name`, { name: neu, reason });
      toast.success('Name korrigiert');
      await refreshManagementAfterMutation(id);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Name konnte nicht geändert werden');
    }
  };

  const recentDrivers = [];
  const seenDriverIds = new Set();
  for (const ride of recentRides) {
    if (!ride.driver_id || seenDriverIds.has(ride.driver_id)) continue;
    seenDriverIds.add(ride.driver_id);
    recentDrivers.push(ride);
    if (recentDrivers.length === 25) break;
  }

  const activeRideDrivers = recentRides.filter(ride =>
    ride.driver_id && ['accepted', 'arrived', 'in_progress'].includes(ride.status)).length;

  const [reviewDocId, setReviewDocId] = useState(null);   // Prüfbereich an Ort und Stelle
  const refresh = () => {
    if (mode === 'recent') loadRecent();
    else {
      if (expanded) setDetailReloadToken(value => value + 1);
      loadManagement();
    }
  };

  const managementRangeStart = managementTotal > 0 ? offset + 1 : 0;
  const managementRangeEnd = Math.min(offset + managementPageSize, managementTotal);
  const hasPreviousPage = offset > 0;
  const hasNextPage = offset + managementPageSize < managementTotal;

  return (
    <div className="drivers-page">
      {reviewDocId && <ReviewDrawerById documentId={reviewDocId} onClose={() => setReviewDocId(null)} onDecided={refresh} />}
      {detailId && (
        <DriverDetailModal
          driverId={detailId}
          onClose={() => setDetailId(null)}
          onNavigateDocument={documentId => setReviewDocId(documentId)}
        />
      )}

      <PageHeader
        title="Fahrer"
        description="Fahrer über aktuelle Einsätze finden oder gezielt verwalten, prüfen und freigeben."
        actions={
        <div className="drivers-heading-actions">
          <span className="drivers-total-badge">
            <UsersRound size={13} />
            {totalDrivers == null ? '—' : Number(totalDrivers).toLocaleString('de-DE')} Gesamtfahrer
          </span>
          <span><ShieldCheck size={13} /> max. {pageSize} sichtbar</span>
          <button onClick={refresh} disabled={mode === 'recent' ? recentLoading : managementLoading}>
            <RefreshCw size={14} />
            {(mode === 'recent' ? recentLoading : managementLoading) ? 'Lädt …' : 'Aktualisieren'}
          </button>
        </div>
      }
      />

      <section className="drivers-mode-switch" aria-label="Fahreransicht">
        <button className={mode === 'recent' ? 'is-active' : ''} onClick={() => setMode('recent')}>
          <Sparkles size={14} />
          <span><strong>Kürzlich relevant</strong><small>Ohne Namen über Einsätze finden</small></span>
        </button>
        <button className={mode === 'management' ? 'is-active' : ''} onClick={() => setMode('management')}>
          <FileCheck2 size={14} />
          <span><strong>Verwaltung & Freigaben</strong><small>Suche, Prüfung und Kontenaktionen</small></span>
        </button>
      </section>

      {mode === 'recent' ? (
        <>
          <section className="drivers-discovery-panel">
            <header>
              <div>
                <span><Clock3 size={15} /></span>
                <div>
                  <small>Discovery-Modus</small>
                  <strong>Aus höchstens 50 aktuellen Fahrten</strong>
                </div>
              </div>
              <p>Mehrfach vorkommende Fahrer werden zusammengeführt; sichtbar bleiben maximal 25 eindeutige Profile.</p>
            </header>

            <div className="drivers-discovery-grid">
              <label className="drivers-context-field">
                <span>Bekannter Zusammenhang</span>
                <div>
                  <Search size={14} />
                  <input
                    value={contextDraft}
                    onChange={event => setContextDraft(event.target.value)}
                    onKeyDown={event => event.key === 'Enter' && applyContextSearch()}
                    placeholder="Kunde, Ort, Partner oder Fahrer …"
                  />
                </div>
              </label>
              <div className="drivers-context-actions">
                <button className="is-search" onClick={applyContextSearch}><Search size={13} /> Eingrenzen</button>
                <button className="is-clear" onClick={clearContextSearch}>Zurücksetzen</button>
              </div>
              <div className="drivers-discovery-stat">
                <small>Eindeutige Fahrer</small>
                <strong>{recentDrivers.length}</strong>
                <span>aus {recentRides.length} Fahrten</span>
              </div>
              <div className="drivers-discovery-stat">
                <small>Aktive Einsätze</small>
                <strong>{activeRideDrivers}</strong>
                <span>im geladenen Ausschnitt</span>
              </div>
            </div>
          </section>

          <section className="drivers-status-strip" aria-label="Letzter Fahrtstatus">
            {recentStatusFilters.map(status => (
              <button
                key={status.value}
                className={recentStatus === status.value ? 'is-active' : ''}
                onClick={() => setRecentStatus(status.value)}
              >
                <i style={{ '--driver-filter-color': RIDE_STATUS_COLORS[status.value] || '#1b1c1f' }} />
                {status.label}
              </button>
            ))}
          </section>

          <section className="drivers-recent-card">
            <header>
              <div>
                <span className="drivers-overline">Fahrer-Discovery</span>
                <h2>Kürzlich relevante Fahrer</h2>
              </div>
              <span><Clock3 size={11} /> neuester Einsatz zuerst</span>
            </header>

            {recentLoading ? (
              <div className="drivers-loading">
                {[0, 1, 2, 3, 4, 5].map(index => <Skeleton key={index} h={45} />)}
              </div>
            ) : recentDrivers.length === 0 ? (
              <Empty
                icon={Truck}
                title="Keine Fahrer im gewählten Ausschnitt"
                hint="Passe den Zusammenhang oder den letzten Fahrtstatus an."
              />
            ) : (
              <div className="drivers-table-scroll">
                <table className="drivers-recent-table">
                  <thead>
                    <tr>
                      <th>Fahrer</th>
                      <th>Letzter Einsatz</th>
                      <th>Kunde & Strecke</th>
                      <th>Partner</th>
                      <th>Fahrtstatus</th>
                      <th>Profil</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentDrivers.map(ride => (
                      <tr key={ride.driver_id}>
                        <td>
                          <div className="drivers-person-cell">
                            <span>{initials(ride.driver_name)}</span>
                            <div>
                              <strong>{ride.driver_name || 'Unbenannter Fahrer'}</strong>
                              <small>{String(ride.driver_id).slice(0, 13)}…</small>
                            </div>
                          </div>
                        </td>
                        <td className="drivers-activity-time">{fmtDate(ride.created_at, true)}</td>
                        <td>
                          <div className="drivers-route-cell">
                            <strong>{ride.customer_name || 'Unbekannter Kunde'}</strong>
                            <span><i /> {ride.pickup_address || '—'}</span>
                            <span><i /> {ride.dropoff_address || '—'}</span>
                          </div>
                        </td>
                        <td className="drivers-partner-cell">{ride.company_name || 'Kein Partner'}</td>
                        <td>
                          <span
                            className="drivers-ride-status"
                            style={{ '--driver-ride-status': RIDE_STATUS_COLORS[ride.status] || '#4b5563' }}
                          >
                            <i /> {RIDE_STATUS_LABELS[ride.status] || ride.status}
                          </span>
                        </td>
                        <td>
                          <button className="drivers-profile-button" onClick={() => setDetailId(ride.driver_id)}>
                            Profil <ArrowRight size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : (
        <>
          <section className="drivers-management-panel">
            <header>
              <div>
                <span><FileCheck2 size={15} /></span>
                <div>
                  <small>Fahrer-Verwaltung</small>
                  <strong>Konten suchen und Statusqueues prüfen</strong>
                </div>
              </div>
              <button className="drivers-create-toggle" onClick={() => setShowNew(value => !value)}>
                {showNew ? <X size={14} /> : <Plus size={14} />}
                {showNew ? 'Abbrechen' : 'Fahrer anlegen'}
              </button>
            </header>

            {showNew && (
              <div className="drivers-create-panel">
                <div>
                  <span className="drivers-overline">Manuelle Anlage</span>
                  <h3>Neuer Fahrer</h3>
                </div>
                <div className="drivers-create-grid">
                  {[
                    ['name', 'Name'],
                    ['email', 'E-Mail'],
                    ['phone', 'Telefon'],
                    ['password', 'Passwort'],
                  ].map(([key, placeholder]) => (
                    <input
                      key={key}
                      aria-label={placeholder}
                      type={key === 'password' ? 'password' : 'text'}
                      placeholder={placeholder}
                      value={newDriver[key]}
                      onChange={event => setNewDriver({ ...newDriver, [key]: event.target.value })}
                    />
                  ))}
                </div>
                <button className="drivers-create-submit" onClick={create}>Fahrer anlegen</button>
              </div>
            )}

            <div className="drivers-management-grid">
              <div className="drivers-approval-filter" aria-label="Freigabestatus">
                {['all', 'pending', 'approved', 'rejected'].map(value => (
                  <button
                    key={value}
                    className={filter === value ? 'is-active' : ''}
                    onClick={() => selectFilter(value)}
                  >
                    {value === 'all' ? 'Alle' : STATUS[value]?.label}
                  </button>
                ))}
              </div>
              <label className="drivers-management-search">
                <span>Optionale Suche</span>
                <div>
                  <Search size={14} />
                  <input
                    value={searchDraft}
                    onChange={event => setSearchDraft(event.target.value)}
                    onKeyDown={event => event.key === 'Enter' && applyManagementSearch()}
                    placeholder="Name, E-Mail oder Telefonnummer …"
                  />
                </div>
              </label>
              <button className="drivers-management-submit" onClick={applyManagementSearch}>
                <Search size={13} /> Suchen
              </button>
              <div className="drivers-management-dates" aria-label="Registrierungszeitraum">
                <label>
                  <span>Registriert von</span>
                  <input
                    aria-label="Registriert von"
                    type="date"
                    value={fromDate}
                    max={toDate || undefined}
                    onChange={event => selectFromDate(event.target.value)}
                  />
                </label>
                <label>
                  <span>Registriert bis</span>
                  <input
                    aria-label="Registriert bis"
                    type="date"
                    value={toDate}
                    min={fromDate || undefined}
                    onChange={event => selectToDate(event.target.value)}
                  />
                </label>
                <button className="drivers-management-reset" onClick={resetManagementFilters}>
                  Filter zurücksetzen
                </button>
              </div>
            </div>
          </section>

          <section className="drivers-directory-card">
            <header>
              <div>
                <span className="drivers-overline">Verwaltungsergebnis</span>
                <h2>Fahrerverzeichnis</h2>
              </div>
              <span>
                <UserRound size={11} /> {managementTotal.toLocaleString('de-DE')} Fahrer insgesamt
              </span>
            </header>

            {managementLoading ? (
              <div className="drivers-loading"><Skeleton h={130} r={10} /></div>
            ) : managementError ? (
              <div className="drivers-directory-error">
                <span>Fahrerverzeichnis konnte nicht geladen werden.</span>
                <button onClick={loadManagement}><RefreshCw size={12} /> Erneut versuchen</button>
              </div>
            ) : drivers.length === 0 ? (
              <Empty
                icon={Truck}
                title="Keine Fahrer im gewählten Ausschnitt"
                hint="Filter oder Suchbegriff anpassen."
              />
            ) : (
              <>
                <div className="drivers-table-scroll">
                  <table className="drivers-directory-table">
                    <thead>
                      <tr>
                        {['Fahrer', 'Rolle', 'Status', 'Registriert', 'Fahrten', 'Aktionen']
                          .map(label => <th key={label}>{label}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {drivers.map(driver => {
                        const status = STATUS[driver.approval_status] || STATUS.pending;
                        const isExpanded = expanded === driver.id;
                        const detailState = managementDetail.id === driver.id
                          ? managementDetail
                          : { id: driver.id, status: 'loading', data: null };
                        return (
                          <React.Fragment key={driver.id}>
                            <tr className={isExpanded ? 'is-expanded' : ''}>
                              <td>
                                <div className="drivers-person-cell">
                                  <span>{initials(driver.name)}</span>
                                  <div>
                                    <strong>{driver.name || 'Unbenannter Fahrer'}</strong>
                                    <small>{String(driver.id).slice(0, 13)}…</small>
                                  </div>
                                </div>
                              </td>
                              <td className="drivers-role-cell">
                                <div className="drivers-role-stack">
                                  <span>{driver.role === 'driver' ? 'Fahrer' : (driver.role || '—')}</span>
                                  {driver.shared_identity?.roles?.length > 1 && (
                                    <small title="Getrennte Konten derselben Person — keine Datenübernahme, kein Sprung">Gleiche E-Mail: {driver.shared_identity.roles
                                      .map(value => ({ customer: 'Kunde', driver: 'Fahrer', partner: 'Partner', admin: 'Admin' }[value] || value))
                                      .join(' · ')} (getrennte Konten)</small>
                                  )}
                                </div>
                              </td>
                              <td>
                                <span
                                  className={`drivers-approval-status is-${driver.approval_status || 'pending'}`}
                                  style={{ '--driver-approval-color': status.color }}
                                >
                                  <i /> {status.label}
                                </span>
                                {driver.suspension && (
                                  <span className={`drivers-suspension-pill ${SUSPENSION_TONE[driver.suspension.kind] || ''}`} title={driver.suspension.short}>
                                    <LockKeyhole size={10} /> {driver.suspension.label}
                                  </span>
                                )}
                              </td>
                              <td className="drivers-registered-cell">{fmtDate(driver.created_at)}</td>
                              <td className="drivers-total-rides-cell">
                                {Number(driver.total_rides || 0).toLocaleString('de-DE')}
                              </td>
                              <td>
                                <div className="drivers-row-actions">
                                  {/* FAHRER-AKTE-ZUGANG-001: die Akte (Überblick, Verdienst, Korrekturbuchung,
                                      Fahrten, Dokumente, Support) war bisher nur über den Heute-Reiter erreichbar —
                                      jetzt direkt aus dem Verzeichnis. */}
                                  <button className="is-akte" onClick={() => setDetailId(driver.id)}>Akte</button>
                                  {driver.approval_status !== 'approved' && (
                                    <button className="is-approve" onClick={() => approve(driver.id)}>Freigeben</button>
                                  )}
                                  {driver.approval_status !== 'rejected' && (
                                    <button className="is-reject" onClick={() => setRejectTarget(driver.id)}>Ablehnen</button>
                                  )}
                                  {/* KASSIERMOMENT-BINDET-001 (Teil C): nur zeigen, wenn der Fahrer
                                      ueberhaupt online steht — sonst gibt es keine Bindung zu loesen. */}
                                  {Number(driver.is_online) === 1 && can('freigaben.manage') && (
                                    <button
                                      className="is-reject"
                                      onClick={() => setDriverOffline(driver.id, driver.name)}
                                      disabled={offlineBusy !== null}
                                      title="Fahrer offline setzen und offene Angebote zurückziehen. Laufende Fahrten bleiben erhalten."
                                    >{offlineBusy === driver.id ? 'Wird offline gesetzt …' : 'Offline setzen'}</button>
                                  )}
                                  <button
                                    className="is-expand"
                                    onClick={() => setExpanded(isExpanded ? null : driver.id)}
                                    aria-label={`${driver.name} Details ${isExpanded ? 'schließen' : 'öffnen'}`}
                                  >
                                    {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                                  </button>
                                  {can('drivers.delete') && (
                                    <button className="is-delete" onClick={() => remove(driver)} title="Fahrer löschen">
                                      <Trash2 size={13} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>

                            {rejectTarget === driver.id && (
                              <tr className="drivers-reject-row">
                                <td colSpan={6}>
                                  <div>
                                    <input
                                      value={rejectReason}
                                      onChange={event => setRejectReason(event.target.value)}
                                      placeholder="Ablehnungsgrund …"
                                    />
                                    <button className="drivers-reject-submit" onClick={() => reject(driver.id)}>Ablehnen</button>
                                    <button onClick={() => { setRejectTarget(null); setRejectReason(''); }}>Abbrechen</button>
                                  </div>
                                </td>
                              </tr>
                            )}

                            {isExpanded && (
                              <tr className="drivers-detail-row">
                                <td colSpan={6}>
                                  <ManagementDriverDetail
                                    state={detailState}
                                    onRetry={() => setDetailReloadToken(value => value + 1)}
                                    onNavigateDocument={documentId => setReviewDocId(documentId)}
                                    onChangeAvatar={changeAvatar}
                                    onOpenAkte={() => setDetailId(driver.id)}
                                    onChangeName={changeName}
                                    canManageLocks={!!(can && can('freigaben.manage'))}
                                    onLock={lockAccount}
                                    onUnlock={unlockAccount}
                                  />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <footer className="drivers-pagination" aria-label="Seitennavigation">
                  <span>{managementRangeStart}–{managementRangeEnd} von {managementTotal.toLocaleString('de-DE')}</span>
                  <div>
                    {/* Seitengröße 25/50/100 — Wechsel springt auf Seite 1 (Owner-Entscheid 26.08.). */}
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#4b5563', marginRight: 8 }}>
                      <select
                        aria-label="Einträge pro Seite"
                        value={pageSize}
                        onChange={(event) => { setPageSize(Number(event.target.value)); setOffset(0); }}
                        style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12, fontWeight: 600, color: '#374151' }}
                      >
                        {[25, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}
                      </select>
                      pro Seite
                    </label>
                    <button
                      disabled={!hasPreviousPage}
                      onClick={() => changePage(offset - managementPageSize)}
                    >
                      Vorherige Seite
                    </button>
                    <button
                      disabled={!hasNextPage}
                      onClick={() => changePage(offset + managementPageSize)}
                    >
                      Nächste Seite
                    </button>
                  </div>
                </footer>
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
}
